// Performance Score V2 — MOAT 03 (ALGORITHMIC)
// 4 sub-scores 0-100. Nulls quando não há dado suficiente (honestidade > número bonito).

import type { AppState, Content, Session } from '@app-types/domain';
import { toLocalDateIso } from './dates';

export interface SubScores {
  execution: number | null; // % de sessões planejadas que foram concluídas (14d)
  consistency: number | null; // dias estudados vs. dias esperados (14d)
  energy: number | null; // média de focus_score auto-relatado (7d)
  learning: number | null; // retenção nas revisões espaçadas (14d) − atraso
}

export interface ScoreResult {
  overall: number | null;
  subs: SubScores;
  /** quantos dias de dado alimentaram o cálculo */
  data_days: number;
}

const WINDOW_EXECUTION_DAYS = 14;
const WINDOW_CONSISTENCY_DAYS = 14;
const WINDOW_ENERGY_DAYS = 7;
const WINDOW_LEARNING_DAYS = 14;

const WEIGHTS = { execution: 0.3, consistency: 0.3, energy: 0.2, learning: 0.2 };

function daysAgo(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function clamp100(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function computeExecution(sessions: Session[], now: Date): number | null {
  const cutoff = daysAgo(now, WINDOW_EXECUTION_DAYS);
  // sessões cujo horário planejado já passou (dentro da janela)
  const due = sessions.filter((s) => {
    const p = new Date(s.planned_at);
    return p >= cutoff && p <= now;
  });
  if (due.length === 0) return null;
  const completed = due.filter((s) => s.status === 'completed').length;
  return clamp100((completed / due.length) * 100);
}

export function computeConsistency(
  sessions: Session[],
  now: Date,
  studyDaysPerWeek: number,
): number | null {
  const cutoff = daysAgo(now, WINDOW_CONSISTENCY_DAYS);
  const completed = sessions.filter(
    (s) => s.status === 'completed' && s.completed_at && new Date(s.completed_at) >= cutoff,
  );
  if (completed.length === 0) return null;
  const uniqueDays = new Set(completed.map((s) => (s.completed_at as string).slice(0, 10))).size;
  const expectedDays = Math.max(1, Math.round((studyDaysPerWeek / 7) * WINDOW_CONSISTENCY_DAYS));
  return clamp100((uniqueDays / expectedDays) * 100);
}

export function computeEnergy(sessions: Session[], now: Date): number | null {
  const cutoff = daysAgo(now, WINDOW_ENERGY_DAYS);
  const scored = sessions.filter(
    (s) =>
      s.status === 'completed' &&
      s.completed_at &&
      new Date(s.completed_at) >= cutoff &&
      typeof s.focus_score === 'number',
  );
  if (scored.length === 0) return null;
  const avg = scored.reduce((sum, s) => sum + (s.focus_score as number), 0) / scored.length;
  // 1-5 → 0-100
  return clamp100(((avg - 1) / 4) * 100);
}

export function computeLearning(contents: Content[], now: Date): number | null {
  const cards = contents.filter((c) => c.review);
  if (cards.length === 0) return null;

  const todayIso = toLocalDateIso(now);
  const cutoff = daysAgo(now, WINDOW_LEARNING_DAYS);
  const reviewed = cards.filter(
    (c) => c.review?.last_reviewed_at && new Date(c.review.last_reviewed_at) >= cutoff,
  );
  const overdue = cards.filter((c) => (c.review as { due_date: string }).due_date < todayIso);
  if (reviewed.length === 0 && overdue.length === 0) return null;

  // retenção: qualidade média das revisões recentes (2=errei → 0 · 5=fácil → 100)
  const base =
    reviewed.length > 0
      ? (reviewed.reduce((sum, c) => sum + ((c.review?.last_quality ?? 3) - 2) / 3, 0) /
          reviewed.length) *
        100
      : 50;
  // pontualidade: cada revisão atrasada desconta
  const penalty = Math.min(40, overdue.length * 8);
  return clamp100(base - penalty);
}

export function computeScore(state: AppState, now = new Date()): ScoreResult {
  const { sessions, contents, settings } = state;

  // dias/semana de estudo estimado a partir de weekly_capacity (2h/dia default) — heurística simples: 5
  const studyDaysPerWeek = Math.min(7, Math.max(3, Math.round(settings.weekly_capacity_h / 2)));

  const subs: SubScores = {
    execution: computeExecution(sessions, now),
    consistency: computeConsistency(sessions, now, studyDaysPerWeek),
    energy: computeEnergy(sessions, now),
    learning: computeLearning(contents, now),
  };

  // overall = média ponderada dos subs disponíveis
  let weightSum = 0;
  let acc = 0;
  if (subs.execution !== null) {
    acc += subs.execution * WEIGHTS.execution;
    weightSum += WEIGHTS.execution;
  }
  if (subs.consistency !== null) {
    acc += subs.consistency * WEIGHTS.consistency;
    weightSum += WEIGHTS.consistency;
  }
  if (subs.energy !== null) {
    acc += subs.energy * WEIGHTS.energy;
    weightSum += WEIGHTS.energy;
  }
  if (subs.learning !== null) {
    acc += subs.learning * WEIGHTS.learning;
    weightSum += WEIGHTS.learning;
  }

  const overall = weightSum > 0 ? clamp100(acc / weightSum) : null;

  // dias distintos com qualquer sessão concluída (para "confiança" do score)
  const dataDays = new Set(
    sessions
      .filter((s) => s.status === 'completed' && s.completed_at)
      .map((s) => (s.completed_at as string).slice(0, 10)),
  ).size;

  return { overall, subs, data_days: dataDays };
}
